"use client"

import * as React from "react"
import {
  COLOR_THEME_STORAGE_KEY,
  resolveColorTheme,
} from "@/shared/utils/color-theme"
export { colorThemeOptions } from "@/shared/utils/color-theme"
export type { ColorTheme } from "@/shared/utils/color-theme"
import type { ColorTheme } from "@/shared/utils/color-theme"

type ColorThemeContextValue = {
  colorTheme: ColorTheme
  setColorTheme: (theme: ColorTheme) => void
}

const ColorThemeContext = React.createContext<ColorThemeContextValue | undefined>(undefined)

export function ColorThemeProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [colorTheme, setColorThemeState] = React.useState<ColorTheme>(() => {
    if (typeof window === "undefined") return "classic"
    return resolveColorTheme(
      window.localStorage.getItem(COLOR_THEME_STORAGE_KEY)
    )
  })

  React.useEffect(() => {
    document.documentElement.dataset.colorTheme = colorTheme
  }, [colorTheme])

  const setColorTheme = React.useCallback((theme: ColorTheme) => {
    setColorThemeState(theme)
    document.documentElement.dataset.colorTheme = theme
    window.localStorage.setItem(COLOR_THEME_STORAGE_KEY, theme)
  }, [])

  const contextValue = React.useMemo(
    () => ({
      colorTheme,
      setColorTheme,
    }),
    [colorTheme, setColorTheme]
  )

  return (
    <ColorThemeContext.Provider value={contextValue}>
      {children}
    </ColorThemeContext.Provider>
  )
}

export function useColorTheme() {
  const context = React.useContext(ColorThemeContext)

  if (!context) {
    throw new Error("useColorTheme must be used within ColorThemeProvider")
  }

  return context
}
